import { apiFetch } from '@/api/apiFetch';
import { useTranslation } from 'react-i18next';

/* ── OAuth Config ──
   Idéntico a Login.jsx a propósito: `COGNITO_REDIRECT_URI` sigue apuntando a
   `.../login`, NO a `.../register`. Cognito solo acepta redirect URIs dados de
   alta en el App Client, y Login.jsx ya intercepta `?code=&state=` al montar
   y completa el alta/login desde ahí — así que reutilizar el mismo destino
   evita tocar la config de Cognito/infra para esto. El usuario que pulse este
   botón desde el registro vuelve por un instante a /login, que termina el
   intercambio y le manda a la app ya con la cuenta creada. */
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || ''; // must be set in .env — no hardcoded fallback
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const COGNITO_REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin + import.meta.env.BASE_URL + 'login' : '';

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 1 12c0 1.94.46 3.77 1.18 5.07l3.66-2.98z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/**
 * Botón "Continuar con Google", compartido entre Login y Register. Pide un
 * `state` de un solo uso al backend (Redis, obligatorio en `/oauth-callback`)
 * y redirige a Cognito. `onError` recibe el mensaje ya traducido para que la
 * página que lo use lo pinte en su propio cuadro de error.
 */
export default function GoogleAuthButton({ onError }) {
  const { t } = useTranslation();

  async function handleClick() {
    onError?.('');
    let state;
    try {
      // /api/auth/* responde el objeto plano, sin envelope `{ data }` — igual
      // que en Login.jsx.
      const res = await apiFetch('/api/auth/oauth-state');
      state = res?.state;
    } catch {
      state = null;
    }
    if (!state) {
      onError?.(t('auth.oauth_state_failed'));
      return;
    }
    window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${COGNITO_CLIENT_ID}`
      + `&redirect_uri=${encodeURIComponent(COGNITO_REDIRECT_URI)}&identity_provider=Google`
      + `&scope=email+openid+profile&state=${encodeURIComponent(state)}`;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={t('auth.sign_in_google')}
      className="vx-btn-oauth sf-chip"
      style={{ width: '100%', height: 48, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: 'hsl(var(--foreground))' }}
    >
      <GoogleLogo /> Google
    </button>
  );
}
